import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getPublicSetting } from '../api/supabaseAPI';
import '../styles/ProgressFloatingPanel.css';

const DEFAULT_AVATAR_URL = '/Resources/imagine/default_avatar/default_avatar.jpeg';
const R2_PUBLIC_BASE_URL_ENV = (import.meta.env.VITE_R2_PUBLIC_BASE_URL || '').replace(/\/$/, '');

const isExternalUrl = (value) => /^https?:\/\//i.test(value || '');

const resolveAvatarUrl = (userData, publicBaseUrl, avatarCacheBust) => {
  const avatarPath = userData?.avatar || '';
  const googleAvatar = userData?.googleAvatarUrl || '';

  if (avatarPath) {
    if (isExternalUrl(avatarPath)) return avatarPath;
    if (!publicBaseUrl) return avatarPath;
    const baseUrl = `${publicBaseUrl}/${avatarPath}`;
    return avatarCacheBust ? `${baseUrl}?v=${avatarCacheBust}` : baseUrl;
  }

  if (googleAvatar) return googleAvatar;
  return DEFAULT_AVATAR_URL;
};

const getLevelInfo = (totalXpRaw) => {
  const totalXp = Math.max(0, Number(totalXpRaw) || 0);

  let level = 1;
  let levelStartXp = 0;
  let xpToNext = 50;

  while (totalXp >= levelStartXp + xpToNext) {
    levelStartXp += xpToNext;
    level += 1;
    xpToNext = 50 * level;
  }

  const xpIntoLevel = totalXp - levelStartXp;
  const progress = xpToNext > 0 ? Math.min(100, Math.max(0, (xpIntoLevel / xpToNext) * 100)) : 0;

  return {
    level,
    xpIntoLevel,
    xpToNext,
    progress,
  };
};

const ProgressFloatingPanel = ({ userData }) => {
  const [avatarCacheBust, setAvatarCacheBust] = useState(() => localStorage.getItem('avatarCacheBust') || '');
  const totalXp = Number(userData?.total_xp) || 0;
  const coins = Number(userData?.current_coins) || 0;
  const { data: r2PublicBaseUrl } = useQuery({
    queryKey: ['public_settings', 'r2_public_base_url'],
    queryFn: () => getPublicSetting('r2_public_base_url'),
    staleTime: 1000 * 60 * 30,
    refetchOnMount: false,
  });

  const publicBaseUrl = useMemo(() => {
    const rawBaseUrl = R2_PUBLIC_BASE_URL_ENV || r2PublicBaseUrl || '';
    return rawBaseUrl ? rawBaseUrl.replace(/\/$/, '') : '';
  }, [r2PublicBaseUrl]);

  const avatarUrl = useMemo(
    () => resolveAvatarUrl(userData, publicBaseUrl, avatarCacheBust),
    [avatarCacheBust, publicBaseUrl, userData],
  );
  const levelInfo = useMemo(() => getLevelInfo(totalXp), [totalXp]);

  useEffect(() => {
    const syncCacheBust = (event) => {
      if (event?.type === 'storage' && event.key !== 'avatarCacheBust') return;
      const nextValue = localStorage.getItem('avatarCacheBust') || '';
      setAvatarCacheBust(nextValue);
    };
    window.addEventListener('storage', syncCacheBust);
    window.addEventListener('avatar-cache-bust', syncCacheBust);
    return () => {
      window.removeEventListener('storage', syncCacheBust);
      window.removeEventListener('avatar-cache-bust', syncCacheBust);
    };
  }, []);

  return (
    <div className="floating-progress-panel" aria-label="User progression">
      <div className="floating-progress-avatar">
        <img
          src={avatarUrl}
          alt="User avatar"
          onError={(event) => {
            event.currentTarget.src = DEFAULT_AVATAR_URL;
          }}
        />
      </div>

      <div className="floating-progress-main">
        <div className="floating-progress-topline">
          <span className="floating-progress-level">Lv. {levelInfo.level}</span>
          <span className="floating-progress-xp">
            {levelInfo.xpIntoLevel}/{levelInfo.xpToNext} XP
          </span>
        </div>
        <progress
          className="floating-progress-bar"
          max={100}
          value={levelInfo.progress}
          aria-label="Level progress"
        />
      </div>

      <div className="floating-progress-coins" title="Coins">
        <svg
          data-testid="geist-icon"
          height="16"
          strokeLinejoin="round"
          viewBox="0 0 16 16"
          width="16"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M8 14.5C11.5899 14.5 14.5 11.5899 14.5 8C14.5 4.41015 11.5899 1.5 8 1.5C4.41015 1.5 1.5 4.41015 1.5 8C1.5 11.5899 4.41015 14.5 8 14.5ZM8 16C12.4183 16 16 12.4183 16 8C16 3.58172 12.4183 0 8 0C3.58172 0 0 3.58172 0 8C0 12.4183 3.58172 16 8 16ZM8.62499 3.375V4V4.375H9C10.1736 4.375 11.125 5.3264 11.125 6.5H9.875C9.875 6.01675 9.48325 5.625 9 5.625H8.62499V7.375H9C10.1736 7.375 11.125 8.3264 11.125 9.5C11.125 10.6736 10.1736 11.625 9 11.625H8.62499V12V12.625H7.37499V12V11.625H7C5.8264 11.625 4.875 10.6736 4.875 9.5H6.125C6.125 9.98325 6.51675 10.375 7 10.375H7.37499V8.625H7C5.8264 8.625 4.875 7.6736 4.875 6.5C4.875 5.3264 5.8264 4.375 7 4.375H7.37499V4V3.375H8.62499ZM7.37499 5.625H7C6.51675 5.625 6.125 6.01675 6.125 6.5C6.125 6.98325 6.51675 7.375 7 7.375H7.37499V5.625ZM8.62499 8.625V10.375H9C9.48325 10.375 9.875 9.98325 9.875 9.5C9.875 9.01675 9.48325 8.625 9 8.625H8.62499Z"
            fill="currentColor"
          />
        </svg>
        <span>{coins}</span>
      </div>
    </div>
  );
};

export default ProgressFloatingPanel;
